import {
  claimVipReward,
  emptyVipSnapshot,
  fetchVipSnapshot,
  formatVipMoney,
  resolveVipIconUrl,
} from './helpers.js';
import { isAccountAbortError } from '../boot/accountCatalogCache.js';
import { phrase } from '../i18n/language.js';

function RankIcon({ url, label }) {
  const src = resolveVipIconUrl(url);
  if (src) {
    return {
      type: 'img',
      src,
      label,
    };
  }
  return { type: 'fallback', label };
}

export function createVipCenterPanel({ useState, useEffect, useLayoutEffect, jsx, jsxs }) {
  function Icon({ url, label }) {
    const spec = RankIcon({ url, label });
    if (spec.type === 'img') {
      return jsx('img', {
        className: 'vip-rank-icon',
        src: spec.src,
        alt: label || '',
      });
    }
    return jsx('span', {
      className: 'vip-rank-icon-fallback',
      'aria-hidden': 'true',
      children: '👑',
    });
  }

  function Metric({ label, row }) {
    if (!row || !row.visible) return null;
    return jsxs('div', {
      className: 'vip-metric',
      children: [
        jsx('div', { className: 'vip-metric-label', children: label }),
        jsxs('div', {
          className: 'info-row info-row-contact vip-progress-row',
          children: [
            jsx('span', { className: 'info-value', children: `${formatVipMoney(row.current)} / ${formatVipMoney(row.required)}` }),
          ],
        }),
        jsx('div', {
          className: 'vip-progress-bar',
          'aria-hidden': 'true',
          children: jsx('i', { style: { width: `${Number(row.percent) || 0}%` } }),
        }),
      ],
    });
  }

  function VipCenterPanel({ t }) {
    const [snap, setSnap] = useState(emptyVipSnapshot);
    const [open, setOpen] = useState(null);
    const [notice, setNotice] = useState('');
    const [vipReady, setVipReady] = useState(false);
    const [vipError, setVipError] = useState('');

    useEffect(() => {
      let cancelled = false;
      setVipError('');
      fetchVipSnapshot()
        .then((next) => {
          if (!cancelled) setSnap(next);
        })
        .catch((err) => {
          if (cancelled || isAccountAbortError(err)) return;
          setVipError(err.message || 'Failed to load');
        })
        .finally(() => {
          if (!cancelled) setVipReady(true);
        });
      return () => {
        cancelled = true;
      };
    }, []);

    const reload = async () => {
      setSnap(await fetchVipSnapshot());
    };

    const flash = (msg) => {
      setNotice(msg);
      window.setTimeout(() => setNotice(''), 1800);
    };

    const onClaim = async (rewardId) => {
      try {
        const result = await claimVipReward(rewardId);
        await reload();
        const amount = result?.rewardAmount ?? result?.reward?.amount ?? result?.amount;
        flash(t('profVipClaimedSuccess', { amount: formatVipMoney(amount) }));
      } catch {
        /* keep existing empty catch style */
      }
    };

    const claimed = snap.claimedRewards || [];
    const rewards = (snap.currentTier?.rewards || []).filter((row) => !claimed.includes(row.id));
    const benefits = snap.currentTier?.benefits || [];
    const name = snap.rankName || snap.currentTier?.label;
    const overallPct = Number(snap.overallProgress?.percent ?? snap.percent) || 0;
    const ranks = Array.isArray(snap.rankList) ? snap.rankList : [];

    return jsxs('div', {
      className: 'panel-personal panel-vip',
      children: [
        !vipReady
          ? jsx('div', { className: 'account-skel-line vip-skel', 'aria-hidden': 'true' })
          : null,
        vipError ? jsx('p', { className: 'account-local-status', children: phrase(vipError) }) : null,
        notice ? jsx('p', { className: 'referral-notice', children: notice }) : null,
        jsx('div', {
          className: 'info-row-wrap',
          children: jsxs('div', {
            className: 'info-row info-row-contact',
            children: [
              jsx('span', { className: 'info-label', children: t('profCurrentVip') }),
              jsxs('span', {
                className: 'vip-rank-head',
                children: [
                  jsx(Icon, { url: snap.iconUrl || snap.currentTier?.iconUrl, label: name }),
                  jsx('span', { className: 'info-verify-badge verified', children: name }),
                ],
              }),
            ],
          }),
        }),
        jsxs('div', {
          className: 'info-row-wrap',
          children: [
            jsxs('div', {
              className: 'info-row info-row-contact vip-progress-row',
              children: [
                jsx('span', { className: 'info-label', children: t('profVipProgress') }),
                jsxs('span', { className: 'info-value', children: [overallPct, '%'] }),
              ],
            }),
            jsx('div', {
              className: 'vip-progress-bar',
              'aria-hidden': 'true',
              children: jsx('i', { style: { width: `${overallPct}%` } }),
            }),
            snap.atMaxRank
              ? jsx('p', { className: 'vip-progress-note', children: t('profVipMaxReached') })
              : null,
            snap.nextRank
              ? jsx('p', {
                  className: 'vip-progress-note',
                  children: t('profNextVipName', { name: snap.nextRank.rankName }),
                })
              : null,
            jsx(Metric, { label: t('profVipDepositProgress'), row: snap.depositProgress }),
            jsx(Metric, { label: t('profVipTurnoverProgress'), row: snap.turnoverProgress }),
          ],
        }),
        benefits.length
          ? jsxs('div', {
              className: 'info-row-wrap',
              children: [
                jsxs('div', {
                  className: 'info-row info-row-contact',
                  children: [
                    jsx('span', { className: 'info-label', children: t('profVipBenefits') }),
                    jsxs('button', {
                      type: 'button',
                      className: 'info-set-btn',
                      onClick: () => setOpen((cur) => (cur === 'benefits' ? null : 'benefits')),
                      children: [benefits.length, ' ›'],
                    }),
                  ],
                }),
                open === 'benefits'
                  ? jsx('div', {
                      className: 'security-list-box',
                      children: benefits.map((item) =>
                        jsx(
                          'div',
                          {
                            className: 'security-list-item vip-benefit-item',
                            children: jsx('strong', { children: item }),
                          },
                          item
                        )
                      ),
                    })
                  : null,
              ],
            })
          : null,
        rewards.length
          ? jsxs('div', {
              className: 'info-row-wrap',
              children: [
                jsxs('div', {
                  className: 'info-row info-row-contact',
                  children: [
                    jsx('span', { className: 'info-label', children: t('profVipRewards') }),
                    jsxs('button', {
                      type: 'button',
                      className: 'info-set-btn',
                      onClick: () => setOpen((cur) => (cur === 'rewards' ? null : 'rewards')),
                      children: [rewards.length, ' ›'],
                    }),
                  ],
                }),
                open === 'rewards'
                  ? jsx('div', {
                      className: 'security-list-box',
                      children: rewards.map((row) =>
                        jsxs(
                          'div',
                          {
                            className: 'security-list-item vip-reward-item',
                            children: [
                              jsxs('div', {
                                children: [
                                  jsx('strong', { children: t('profVipRewardUpgrade') }),
                                  jsx('span', { className: 'security-tag ok', children: formatVipMoney(row.amount) }),
                                ],
                              }),
                              jsx('button', {
                                type: 'button',
                                className: 'info-set-btn',
                                onClick: () => onClaim(row.id),
                                children: t('profVipClaim'),
                              }),
                            ],
                          },
                          row.id
                        )
                      ),
                    })
                  : null,
              ],
            })
          : null,
        ranks.length
          ? jsxs('div', {
              className: 'info-row-wrap',
              children: [
                jsx('div', {
                  className: 'info-row info-row-contact',
                  children: jsx('span', { className: 'info-label', children: t('profVipLadder') }),
                }),
                jsx('div', {
                  className: 'vip-ladder',
                  children: ranks.map((row) =>
                    jsxs(
                      'div',
                      {
                        className: `vip-ladder-row is-${row.state || 'locked'}`,
                        children: [
                          jsx(Icon, { url: row.iconUrl, label: row.rankName }),
                          jsxs('div', {
                            className: 'vip-ladder-copy',
                            children: [
                              jsx('strong', { children: row.rankName }),
                              jsx('small', {
                                children: t('profVipLadderReq', {
                                  deposit: formatVipMoney(row.depositRequirement),
                                  turnover: formatVipMoney(row.turnoverRequirement),
                                }),
                              }),
                              Number(row.rewardAmount) > 0
                                ? jsx('small', { children: t('profVipLadderReward', { amount: formatVipMoney(row.rewardAmount) }) })
                                : null,
                            ],
                          }),
                          jsx('span', {
                            className: 'vip-state',
                            children:
                              row.state === 'current'
                                ? t('profVipStateCurrent')
                                : row.state === 'completed'
                                  ? t('profVipStateCompleted')
                                  : t('profVipStateLocked'),
                          }),
                        ],
                      },
                      row.rankId
                    )
                  ),
                }),
              ],
            })
          : null,
      ],
    });
  }

  return VipCenterPanel;
}
